/**
 * <track-order-form>
 *
 * Drives the guest "Track Your Order" form. On submit it POSTs the order number and
 * email/phone the customer entered to a Shopify App Proxy endpoint (see the
 * `data-endpoint` attribute, configurable from the section's theme editor settings).
 *
 * This component never talks to the Shopify Admin API directly — that must happen
 * server-side, behind an App Proxy, so the store's Admin API access token is never
 * exposed to the browser. Until that endpoint exists, requests will fail and the
 * customer will correctly see the "order not found" message, since there is no real
 * data source connected yet.
 *
 * Expected endpoint contract
 * --------------------------
 * POST { endpoint }
 * Body (JSON): { "order_number": "#1001", "contact": "jane@example.com" }
 *
 * 200 OK, order found:
 * {
 *   "success": true,
 *   "order": {
 *     "name": "#1001",
 *     "created_at": "2026-09-20T10:00:00Z",
 *     "cancelled": false,
 *     "status_stage": "shipped",   // optional explicit override — one of:
 *                                  // confirmed | processing | shipped | out_for_delivery | delivered
 *     "financial_status": "paid",
 *     "fulfillment_status": "fulfilled",
 *     "fulfillments": [
 *       {
 *         "tracking_number": "1Z999AA10123456784",
 *         "tracking_url": "https://www.ups.com/track?tracknum=1Z999AA10123456784",
 *         "tracking_company": "UPS",
 *         "shipment_status": "in_transit"
 *       }
 *     ]
 *   }
 * }
 *
 * 200 OK / 404, order not found:
 * { "success": false }
 *
 * Any other response (including a 404 because no app proxy is installed yet) is
 * treated as "order not found" so the customer always sees a friendly message.
 */

const STAGES = ["confirmed", "processing", "shipped", "out_for_delivery", "delivered"];

function deriveStage(order) {
  if (order.status_stage && STAGES.includes(order.status_stage)) {
    return order.status_stage;
  }

  const shipmentStatuses = (order.fulfillments || [])
    .map((fulfillment) => fulfillment.shipment_status)
    .filter(Boolean);

  if (shipmentStatuses.includes("delivered")) return "delivered";
  if (shipmentStatuses.includes("out_for_delivery")) return "out_for_delivery";

  if (order.fulfillment_status === "fulfilled" || order.fulfillment_status === "partial") {
    return "shipped";
  }

  if (order.financial_status === "paid") {
    return "processing";
  }

  return "confirmed";
}

class TrackOrderForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.statusEl = this.querySelector("[data-track-order-status]");
    this.loadingEl = this.querySelector("[data-track-order-loading]");
    this.errorEl = this.querySelector("[data-track-order-error]");
    this.resultEl = this.querySelector("[data-track-order-result]");
    this.timelineEl = this.querySelector("[data-track-order-timeline]");
    this.trackingEl = this.querySelector("[data-track-order-tracking]");

    this.notFoundMessage = this.dataset.orderNotFoundMessage;
    this.genericErrorMessage = this.dataset.genericErrorMessage;
    this.endpoint = this.dataset.endpoint;

    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.lookup();
    });
  }

  async lookup() {
    const formData = new FormData(this.form);
    const orderNumber = (formData.get("order_number") || "").toString().trim();
    const contact = (formData.get("contact") || "").toString().trim();

    if (!orderNumber || !contact) {
      this.form.reportValidity();
      return;
    }

    this.showLoading();

    try {
      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ order_number: orderNumber, contact }),
      });

      if (!response.ok) {
        this.showError(this.notFoundMessage);
        return;
      }

      const data = await response.json();

      if (!data || !data.success || !data.order) {
        this.showError(this.notFoundMessage);
        return;
      }

      this.showResult(data.order);
    } catch (error) {
      console.warn(
        "[track-order] Lookup request failed. This usually means no order-tracking app proxy is installed yet at",
        this.endpoint,
        error
      );
      this.showError(this.genericErrorMessage || this.notFoundMessage);
    }
  }

  showLoading() {
    this.statusEl.hidden = false;
    this.loadingEl.hidden = false;
    this.errorEl.hidden = true;
    this.resultEl.hidden = true;
  }

  showError(message) {
    this.loadingEl.hidden = true;
    this.resultEl.hidden = true;
    this.errorEl.hidden = false;
    this.errorEl.textContent = message;
  }

  showResult(order) {
    this.loadingEl.hidden = true;
    this.errorEl.hidden = true;
    this.resultEl.hidden = false;

    if (order.cancelled) {
      this.showError(this.genericErrorMessage || this.notFoundMessage);
      return;
    }

    const nameEl = this.querySelector("[data-track-order-name]");
    const dateEl = this.querySelector("[data-track-order-date]");
    if (nameEl) nameEl.textContent = order.name || "";
    if (dateEl && order.created_at) {
      dateEl.textContent = new Date(order.created_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }

    const stage = deriveStage(order);
    const stageIndex = STAGES.indexOf(stage);

    this.timelineEl.querySelectorAll("[data-step]").forEach((step) => {
      const index = STAGES.indexOf(step.dataset.step);
      step.classList.toggle("is-complete", index <= stageIndex);
      step.classList.toggle("is-current", index === stageIndex);
    });

    const fulfillment = (order.fulfillments || []).find((f) => f.tracking_number);

    if (fulfillment) {
      this.trackingEl.hidden = false;
      const numberEl = this.querySelector("[data-track-order-tracking-number]");
      const linkEl = this.querySelector("[data-track-order-tracking-link]");
      if (numberEl) numberEl.textContent = fulfillment.tracking_number;

      if (linkEl) {
        if (fulfillment.tracking_url) {
          linkEl.href = fulfillment.tracking_url;
          linkEl.hidden = false;
        } else {
          linkEl.hidden = true;
        }
      }
    } else {
      this.trackingEl.hidden = true;
    }
  }
}

if (!window.customElements.get("track-order-form")) {
  window.customElements.define("track-order-form", TrackOrderForm);
}
